import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useGlobalContext } from '../../../../../utils/Context'
import { deleteAPI, get, post } from '../../../../../utils/Api'
import PageLoader from '../../../../../components/loader/PageLoader'
import { Ctx } from './ctx'
import type { BookingCtx } from './ctx'
import { Ic, Note } from './fields'
import { buildBookingPayload, buildDraftPayload, buildPriceData, buildQuoteBody, formFromBooking, formFromLegacy } from './payload'
import ItemsStep from './ItemsStep'
import PartyStep from './PartyStep'
import ReviewStep from './ReviewStep'
import type { FinalState } from './ReviewStep'
import RouteStep, { toCustomer } from './RouteStep'
import Ticket, { priceShown } from './Ticket'
import type { EstimateState, PriceInput } from './Ticket'
import { addressReady, blankForm, formKind, naira, num, ownerSide, stepsFor, validate } from './rules'
import type { BookingForm, Country, Customer, Errors, Kind, Step } from './types'
import './BookShipment.scss'

const TITLES: Record<Step, string> = {
  route: 'Route & parcel',
  sender: 'Sender',
  receiver: 'Receiver',
  items: 'Items & customs',
  review: 'Review & pay',
}

/**
 * Scroll inside the dashboard's own scroller (.main-body) rather than letting
 * scrollIntoView also move the window, which pushes the topbar off screen.
 */
const reveal = (el: Element | null | undefined, block: 'start' | 'center') => {
  if (!el) return
  const box = el.closest('.main-body')
  const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!box) return el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block })
  const r = el.getBoundingClientRect()
  const b = box.getBoundingClientRect()
  const top =
    block === 'start' ? box.scrollTop + r.top - b.top - 96 : box.scrollTop + r.top - b.top - (b.height - r.height) / 2
  box.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' })
}

/*
 * Refresh-proof form, without a draft. The form is kept in sessionStorage,
 * tagged with the history entry it was opened in. A refresh keeps that entry
 * (react-router's location.key lives in history.state), so the copy is
 * restored. Opening the form again from the app creates a new entry, so the
 * copy doesn't match and is cleared. sessionStorage is per tab and goes when
 * the tab closes.
 */
const STORE_KEY = 'qc-admin-booking-form'
type Saved = {
  navKey: string
  page: string
  f: BookingForm
  customer: Customer | null
  current: Step
  done: Partial<Record<Step, boolean>>
  draft: BookingCtx['draft']
}
const isReload = () =>
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type === 'reload'
function readSaved(navKey: string, page: string): Saved | null {
  try {
    const s: Saved | null = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null')
    // 'default' means no history state (a typed URL): only trust it on an actual reload
    if (s && s.navKey === navKey && s.page === page && (navKey !== 'default' || isReload())) return s
  } catch {
    /* unreadable or blocked storage: start blank */
  }
  return null
}
const clearSaved = () => {
  try {
    sessionStorage.removeItem(STORE_KEY)
  } catch {
    /* storage blocked */
  }
}

const apiMessage = (r: any, fallback: string) =>
  typeof r?.message === 'string' ? r.message : r?.message?.detail || fallback

export default function BookShipment({ kind }: { kind: Kind }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const draftId = new URLSearchParams(location.search).get('draftId')
  const { notify, profile } = useGlobalContext()

  const page = location.pathname + location.search
  const [restored] = useState(() => readSaved(location.key, page))
  const [f, setF] = useState<BookingForm>(() => restored?.f || blankForm(kind))
  const [customer, setCustomer] = useState<Customer | null>(restored?.customer || null)
  const [countries, setCountries] = useState<Country[]>([])
  const [errs, setErrs] = useState<Partial<Record<Step, Errors>>>({})
  const [current, setCurrent] = useState<Step>(restored?.current || 'route')
  const [done, setDone] = useState<Partial<Record<Step, boolean>>>(restored?.done || {})
  const [estimate, setEstimate] = useState<EstimateState>({ status: 'idle' })
  const [final, setFinal] = useState<FinalState>(null)
  const [booting, setBooting] = useState(!restored && !!(draftId || id))
  const [draft, setDraft] = useState<BookingCtx['draft']>(restored?.draft || null)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [draftSavedAt, setDraftSavedAt] = useState('')
  const [booked, setBooked] = useState<any>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [bbOpen, setBbOpen] = useState(false)
  const [live, setLive] = useState('')

  const steps = stepsFor(f)
  const onReview = current === 'review' && !booked
  const say = (msg: string) => {
    setLive('')
    setTimeout(() => setLive(msg), 30)
  }

  /* ---------- State helpers ---------- */

  const set = useCallback((fn: (d: BookingForm) => void) => {
    setF((prev) => {
      const d = structuredClone(prev)
      fn(d)
      return d
    })
  }, [])
  const err = (key: string) => {
    for (const s of Object.values(errs)) if (s && s[key]) return s[key]
    return undefined
  }
  const clearErr = (...keys: string[]) =>
    setErrs((prev) => {
      let changed = false
      const next: typeof prev = {}
      for (const [s, e] of Object.entries(prev) as [Step, Errors][]) {
        const copy = { ...e }
        for (const key of keys) if (key && copy[key]) {
          delete copy[key]
          changed = true
        }
        next[s] = copy
      }
      return changed ? next : prev
    })

  /** Pull the full customer record: balance, credit and the wallet's funding account. */
  const loadCustomer = (userId: string) =>
    get(`user/view/${userId}`).then((r: any) => {
      if (r.status === 200 && r.data) {
        const c = toCustomer(r.data)
        setCustomer(c)
        return c
      }
      return null
    })

  const pickCustomer = (c: Customer | null) => {
    setCustomer(c)
    clearErr('cust')
    set((d) => {
      d.pay = ''
      // The account-holder details belong to the previous customer
      const side = ownerSide(d)
      if (d[side].mode === 'owner') {
        d[side].mode = ''
        Object.assign(d[side], { first: '', last: '', phone: '', email: '' })
      }
    })
    if (c) {
      loadCustomer(c.value).catch(() => {})
      setTimeout(() => document.getElementById('cust-change')?.focus(), 0)
    } else setTimeout(() => document.getElementById('cust')?.focus(), 0)
  }

  /* ---------- Load: countries, then a draft / duplicate / quote prefill ---------- */

  useEffect(() => {
    get('utils/get-countries')
      .then((r: any) => {
        if (r.status === 200 && Array.isArray(r.data))
          setCountries(
            r.data
              .map((c: any) => ({ name: c.name, isoCode: c.isoCode, phoneCode: c.phoneCode }))
              .sort((a: Country, b: Country) => (a.isoCode === 'NG' ? -1 : b.isoCode === 'NG' ? 1 : a.name.localeCompare(b.name))),
          )
        else notify('error', apiMessage(r, 'Failed to fetch countries'))
      })
      .catch(() => notify('error', 'Failed to fetch countries'))
  }, [])

  useEffect(() => {
    if (restored) {
      // Same form after a refresh: only re-read the customer for a current balance
      if (restored.customer) loadCustomer(restored.customer.value).catch(() => {})
      return
    }
    clearSaved()
    if (draftId) {
      get(`booking/fetch-draft/${draftId}`)
        .then((r: any) => {
          if (r.status !== 200) return notify('error', apiMessage(r, 'Failed to load the draft'))
          const d = r.data
          setF(formFromLegacy(d, kind))
          setDraft({
            id: d.drfID || draftId,
            by: d.creatorName || d.userName || 'the customer',
            kg: (d.packages || []).map((p: any) => num(p.weight)),
          })
          return loadCustomer(d.userId)
        })
        .catch(() => notify('error', 'Failed to load the draft'))
        .finally(() => setBooting(false))
      return
    }
    if (id) {
      get(`admin/booking/${id}`)
        .then((r: any) => {
          if (r.status !== 200) return notify('error', apiMessage(r, 'Failed to load the booking'))
          setF(formFromBooking(r.data, kind))
          return loadCustomer(r.data.userId)
        })
        .catch(() => notify('error', 'Failed to load the booking'))
        .finally(() => setBooting(false))
      return
    }
    // Quick Quote → "Convert to booking"
    const raw = sessionStorage.getItem('quotePrefill')
    if (!raw) return
    try {
      const prefill = JSON.parse(raw)
      setF(formFromLegacy(prefill, kind))
      if (prefill.selectedUser?.value) {
        setCustomer(prefill.selectedUser)
        loadCustomer(prefill.selectedUser.value).catch(() => {})
      }
    } catch {
      /* ignore malformed prefill */
    } finally {
      sessionStorage.removeItem('quotePrefill')
    }
  }, [draftId, id])

  useEffect(() => {
    if (booting) return
    if (booked) return clearSaved()
    try {
      const saved: Saved = { navKey: location.key, page, f, customer, current, done, draft }
      sessionStorage.setItem(STORE_KEY, JSON.stringify(saved))
    } catch {
      /* storage full or blocked: the form still works, it just won't survive a refresh */
    }
  }, [f, customer, current, done, draft, booting, booked])

  /* ---------- Live estimate (price/customer-quote) ---------- */

  const k = formKind(f)
  const routeReady = !!k && k !== 'invalid' && (k !== 'domestic' || (!!f.fromState && !!f.toState))
  const parcelsReady = f.parcels.every((p) => num(p.w) > 0 && num(p.l) > 0 && num(p.wd) > 0 && num(p.h) > 0)
  const senderReady = addressReady(f, 'sender', customer)
  const receiverReady = addressReady(f, 'receiver', customer)
  // What the price depends on — shown as a checklist while any of it is missing
  const priceInputs: PriceInput[] = [
    { key: 'customer', label: 'The customer', short: 'Customer', done: !!customer },
    { key: 'route', label: k === 'domestic' ? 'Both states' : 'The route', short: k === 'domestic' ? 'States' : 'Route', done: routeReady },
    { key: 'parcel', label: 'Parcel weight and size', short: 'Parcel', done: parcelsReady },
    { key: 'sender', label: 'The sender’s address', short: 'Sender address', done: senderReady },
    { key: 'receiver', label: 'The receiver’s address', short: 'Receiver address', done: receiverReady },
  ]
  const canQuote = !!customer && routeReady && parcelsReady
  const quoteBody = useMemo(
    () => (canQuote ? JSON.stringify({ ...buildQuoteBody(f, customer), plannedShippingDateAndTime: undefined }) : ''),
    [f, customer, canQuote],
  )
  const estSeq = useRef(0)
  useEffect(() => {
    if (onReview) return // the final price takes over on the last step
    if (!quoteBody) {
      setEstimate({ status: 'idle' })
      return
    }
    const mine = ++estSeq.current
    const exact = senderReady && receiverReady
    const held = (prev: EstimateState) =>
      prev.status === 'ok' || prev.status === 'loading' || prev.status === 'waiting' ? prev.price : null
    setEstimate((prev) => ({ status: 'loading', price: held(prev) }))
    const t = setTimeout(() => {
      post('price/customer-quote', buildQuoteBody(f, customer), null)
        .then((r: any) => {
          if (mine !== estSeq.current) return
          if (r.status === 200 && r.success !== false) {
            setEstimate({ status: 'ok', price: r.data, exact })
            say(`${exact ? 'Estimated total' : 'Route estimate'} ${naira(num(r.data.totalPrice))}`)
          } else if (exact) setEstimate({ status: 'error', message: apiMessage(r, '') })
          // Details are still coming in: wait for them rather than show the API's complaint
          else setEstimate((prev) => ({ status: 'waiting', price: held(prev) }))
        })
        .catch(() => mine === estSeq.current && setEstimate((prev) => ({ status: 'waiting', price: held(prev) })))
    }, 600)
    return () => clearTimeout(t)
    // After a failed attempt, try again as each address comes in, even if the priced body is unchanged
  }, [quoteBody, onReview, estimate.status === 'waiting' ? `${senderReady}${receiverReady}` : ''])

  /* ---------- Final price (price/admin-single), automatic on the review step ---------- */

  const priceKey = useMemo(
    () => (onReview && customer && countries.length ? JSON.stringify({ ...buildPriceData(f, customer, countries), plannedShippingDateAndTime: undefined }) : ''),
    [onReview, f, customer, countries],
  )
  const finalSeq = useRef(0)
  const fetchFinal = useCallback(() => {
    if (!customer) return
    const mine = ++finalSeq.current
    setFinal({ status: 'loading' })
    post('price/admin-single', buildPriceData(f, customer, countries), null)
      .then((r: any) => {
        if (mine !== finalSeq.current) return
        if (r.status === 200 && r.data) {
          setFinal({ status: 'ok', price: r.data })
          say(`Final price ${naira(num(r.data.totalPrice))}`)
        } else {
          setFinal({ status: 'error', message: apiMessage(r, '') })
          say('We couldn’t get the final price.')
        }
      })
      .catch(() => mine === finalSeq.current && setFinal({ status: 'error', message: '' }))
  }, [f, customer, countries])
  useEffect(() => {
    if (!priceKey) {
      finalSeq.current++
      setFinal(null)
      return
    }
    setFinal({ status: 'loading' })
    const t = setTimeout(fetchFinal, 300)
    return () => clearTimeout(t)
  }, [priceKey])

  // A payment option that stops being available (wallet now short, etc.) is unselected
  const total = final?.status === 'ok' ? num(final.price.totalPrice) : 0
  useEffect(() => {
    if (!f.pay || !customer || final?.status !== 'ok') return
    const bad =
      (f.pay === 'wallet' && customer.balance < total) ||
      (f.pay === 'credit' && (!customer.turnoff_payment || customer.creditLimit - customer.debt < total))
    if (bad) set((d) => void (d.pay = ''))
  }, [total, customer, f.pay])

  /* ---------- Navigation ---------- */

  const goTo = (step: Step) => {
    setCurrent(step)
    setBbOpen(false)
    setTimeout(() => {
      const h = document.getElementById(`bk-h-${step}`)
      h?.focus({ preventScroll: true })
      reveal(h, 'start')
    }, 0)
  }
  const focusFirstError = (step: Step) =>
    setTimeout(() => {
      const sec = document.getElementById(`bk-sec-${step}`)
      const e = sec?.querySelector('.bk-err, .bk-meter.over')
      if (!e) return
      let target: HTMLElement | null | undefined
      if (e.closest('#meter-w')) target = sec?.querySelector<HTMLElement>('[id$="-weight"]')
      else if (e.closest('#meter-v')) target = sec?.querySelector<HTMLElement>('[id$="-price"]')
      else {
        const wrap = e.closest('.bk-field, fieldset, .bk-grp')
        target = wrap?.querySelector<HTMLElement>('[aria-invalid="true"], input:not([type=hidden]):not(.bk-file), select, textarea, button')
      }
      target?.focus({ preventScroll: true })
      reveal(target, 'center')
    }, 0)

  const check = (step: Step) => {
    const e = validate(step, f, customer)
    setErrs((prev) => ({ ...prev, [step]: e }))
    const n = Object.keys(e).length
    if (n) {
      setDone((d) => ({ ...d, [step]: false }))
      focusFirstError(step)
      say(`${n} ${n > 1 ? 'things need' : 'thing needs'} fixing before you continue.`)
    }
    return n === 0
  }

  const proceed = () => {
    if (!check(current)) return
    setDone((d) => ({ ...d, [current]: true }))
    if (current === 'review') return submit()
    goTo(steps[steps.indexOf(current) + 1] || 'review')
  }

  /* ---------- Book, save draft ---------- */

  const submit = () => {
    for (const s of steps) {
      const e = validate(s, f, customer)
      if (Object.keys(e).length) {
        setErrs((prev) => ({ ...prev, [s]: e }))
        setDone((d) => ({ ...d, [s]: false }))
        goTo(s)
        focusFirstError(s)
        say(`${TITLES[s]} needs attention before you can book.`)
        return
      }
    }
    if (final?.status !== 'ok' || !customer) return say('Wait for the final price first.')
    setSubmitting(true)
    post(
      'admin/create-booking',
      buildBookingPayload({
        f,
        customer,
        countries,
        price: final.price,
        paymentMethod: f.pay,
        location: profile?.location,
        draftId,
      }),
      null,
    )
      .then((r: any) => {
        if (r.status === 200) {
          notify('success', apiMessage(r, 'Booking created'))
          setBooked(r.data)
          setTimeout(() => {
            document.getElementById('bk-succ-h')?.focus()
            document.querySelector('.main-body')?.scrollTo({ top: 0 })
          }, 0)
        } else notify('error', apiMessage(r, 'The booking failed'))
      })
      .catch(() => notify('error', 'The booking failed. Check your connection and try again.'))
      .finally(() => setSubmitting(false))
  }

  const saveDraft = () => {
    if (!customer) {
      setErrs((prev) => ({ ...prev, route: { ...(prev.route || {}), cust: 'Choose the customer before saving a draft.' } }))
      goTo('route')
      focusFirstError('route')
      return
    }
    // The draft model requires the parcel, sender and receiver details
    for (const s of ['route', 'sender', 'receiver'] as Step[]) {
      const e = validate(s, f, customer)
      if (Object.keys(e).length) {
        setErrs((prev) => ({ ...prev, [s]: e }))
        notify('error', `Finish ${TITLES[s].toLowerCase()} before saving a draft.`)
        if (current !== s) goTo(s)
        focusFirstError(s)
        return
      }
    }
    setSavingDraft(true)
    post('booking/create-draft', buildDraftPayload(f, customer, profile?.name), null)
      .then((r: any) => {
        if (r.status >= 200 && r.status < 300) {
          notify('success', apiMessage(r, 'Draft saved'))
          setDraftSavedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
        } else notify('error', apiMessage(r, 'Couldn’t save the draft'))
      })
      .catch(() => notify('error', 'Couldn’t save the draft'))
      .finally(() => setSavingDraft(false))
  }

  const deleteDraft = () => {
    if (!draftId) return
    deleteAPI(`booking/draft/${draftId}`)
      .then((r: any) => {
        if (r.status === 200) {
          notify('success', apiMessage(r, 'Draft deleted'))
          navigate('/admin/drafts')
        } else notify('error', apiMessage(r, 'Couldn’t delete the draft'))
      })
      .catch(() => notify('error', 'Couldn’t delete the draft'))
  }

  const refreshBalance = () => {
    if (!customer) return
    setRefreshing(true)
    loadCustomer(customer.value)
      .then((c) => c && say(`Balance ${naira(c.balance)}`))
      .catch(() => {})
      .finally(() => setRefreshing(false))
  }

  const startAgain = (same: boolean) => {
    const keep = same ? customer : null
    setF(blankForm(kind))
    setErrs({})
    setDone({})
    setFinal(null)
    setBooked(null)
    setDraft(null)
    setCurrent('route')
    setCustomer(keep)
    if (keep) loadCustomer(keep.value).catch(() => {})
    navigate(`/admin/${kind}`, { replace: true })
  }

  /* ---------- Render ---------- */

  const ctx: BookingCtx = { f, set, err, clearErr, customer, pickCustomer, countries, notify, draft }
  const shown = priceShown(estimate, final, onReview)
  const primaryLabel = () => {
    if (current !== 'review') return 'Continue'
    if (submitting) return 'Booking…'
    if (final?.status !== 'ok') return final?.status === 'error' ? 'Final price unavailable' : 'Getting final price…'
    const t = naira(total)
    return f.pay === 'offline' ? `Book — paid offline` : f.pay === 'credit' ? `Book on credit · ${t}` : `Book & charge ${t}`
  }
  const primaryDisabled = current === 'review' && (final?.status !== 'ok' || submitting)
  const nextName = current !== 'review' ? TITLES[steps[steps.indexOf(current) + 1] || 'review'] : ''

  if (booting)
    return (
      <div className='home'>
        <PageLoader position='center' />
      </div>
    )

  if (booked) {
    const trackingId = booked?.shipmentMeta?.trackingId
    const c = customer
    return (
      <Ctx.Provider value={ctx}>
        <div className='bk-page single'>
          <div className='bk-success'>
            <div className='succ-top'>
              <div className='tick'>
                <Ic name='check' />
              </div>
              <h2 id='bk-succ-h' tabIndex={-1}>
                Shipment booked
              </h2>
              <p>
                {c ? `For ${c.firstname} ${c.lastname} · ` : ''}
                {naira(total)}{' '}
                {f.pay === 'offline' ? 'recorded as paid offline' : f.pay === 'credit' ? 'added to credit' : 'charged to their wallet'}
                {draft ? ` · from draft ${draft.id}` : ''}
              </p>
              {trackingId && (
                <div className='trk'>
                  <span>Tracking ID</span>
                  <b>{trackingId}</b>
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard?.writeText(String(trackingId))
                      say('Tracking ID copied')
                    }}
                  >
                    <Ic name='copy' /> Copy
                  </button>
                </div>
              )}
            </div>
            <div className='succ-b'>
              <h3 className='bk-grp-title'>What happens next</h3>
              <ol>
                <li>
                  <span className='oi'>
                    <Ic name={f.handover.mode === 'pickup' ? 'truck' : 'store'} />
                  </span>
                  <p>
                    {f.handover.mode === 'pickup'
                      ? k === 'import'
                        ? 'DHL collects from the sender on the chosen date.'
                        : 'A rider collects on the chosen date, between 9am and 5pm.'
                      : k === 'import'
                        ? 'The sender drops the parcel at DHL.'
                        : 'The parcel goes out on the next dispatch.'}
                  </p>
                </li>
                <li>
                  <span className='oi'>
                    <Ic name='message-square' />
                  </span>
                  <p>Tracking updates go to the receiver{f.receiver.email ? ' by text and email' : ''}.</p>
                </li>
              </ol>
              <div className='succ-actions'>
                <button
                  type='button'
                  className='bk-btn primary'
                  onClick={() => navigate(booked?._id ? `/admin/booking/${booked._id}` : `/admin/floating-booking/${booked}`)}
                >
                  Open booking
                </button>
                {c && (
                  <button type='button' className='bk-btn secondary' onClick={() => startAgain(true)}>
                    New booking for {c.firstname}
                  </button>
                )}
                <button type='button' className='bk-btn quiet' onClick={() => startAgain(false)}>
                  New booking
                </button>
                {draftId && (
                  <button type='button' className='bk-btn danger-quiet' onClick={deleteDraft}>
                    Delete draft
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className='bk-sr' aria-live='polite'>
            {live}
          </div>
        </div>
      </Ctx.Provider>
    )
  }

  const ci = Math.max(0, steps.indexOf(current))
  const firstOpen = steps.findIndex((s) => !done[s])

  return (
    <Ctx.Provider value={ctx}>
      <div className='bk-page'>
        <div className='bk-main'>
          <button type='button' className='bk-back' onClick={() => (ci === 0 ? navigate(draftId ? '/admin/drafts' : '/admin/home') : goTo(steps[ci - 1]))}>
            <Ic name='arrow-left' /> {ci === 0 ? 'Back' : `Back to ${TITLES[steps[ci - 1]]}`}
          </button>
          <div className='bk-head'>
            <div>
              <h1>{draft ? `Complete draft ${draft.id}` : id ? 'Duplicate booking' : 'New booking'}</h1>
              <p>
                {draft
                  ? `Submitted by ${draft.by}. Check everything against the parcel, then charge.`
                  : 'Book a shipment on a customer’s behalf.'}
              </p>
            </div>
            <div className='bk-head-actions'>
              {draftSavedAt && (
                <span className='bk-saved' role='status'>
                  <Ic name='check' /> Draft saved {draftSavedAt}
                </span>
              )}
              <button type='button' className='bk-btn secondary sm' onClick={saveDraft} disabled={savingDraft} aria-busy={savingDraft}>
                {savingDraft ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </div>

          <ol className='bk-stepper' aria-label='Booking progress'>
            {steps.map((s, i) => {
              const isCur = s === current
              const isDone = !!done[s]
              const can = isDone || i <= (firstOpen < 0 ? steps.length : firstOpen)
              return (
                <li key={s} className={`${isCur ? 'cur' : ''} ${isDone && !isCur ? 'done' : ''}`}>
                  <button type='button' disabled={!can || isCur} aria-current={isCur ? 'step' : undefined} onClick={() => goTo(s)}>
                    <span className='bar' />
                    <span className='lab'>
                      <span className='n' aria-hidden='true'>
                        {isDone && !isCur ? <Ic name='check' /> : i + 1}
                      </span>
                      {TITLES[s]}
                      {isDone && <span className='bk-sr'>, completed</span>}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>
          <div className='bk-step-compact' aria-hidden='true'>
            <p>
              Step {ci + 1} of {steps.length} · <b>{TITLES[current]}</b>
            </p>
            <div className='track'>
              <div className='fill' style={{ width: `${((ci + 1) / steps.length) * 100}%` }} />
            </div>
          </div>

          <section className='bk-sec' id={`bk-sec-${current}`} aria-labelledby={`bk-h-${current}`}>
            <div className='bk-sec-head'>
              <h2 id={`bk-h-${current}`} tabIndex={-1}>
                <span className='n' aria-hidden='true'>
                  {ci + 1}
                </span>
                {TITLES[current]}
              </h2>
            </div>
            <div className='bk-sec-body'>
              {current === 'route' && <RouteStep />}
              {current === 'sender' && <PartyStep who='sender' />}
              {current === 'receiver' && <PartyStep who='receiver' />}
              {current === 'items' && <ItemsStep />}
              {current === 'review' && (
                <ReviewStep
                  final={final}
                  estimate={estimate.status === 'ok' ? estimate.price : estimate.status === 'loading' ? estimate.price : null}
                  onRetry={fetchFinal}
                  goTo={goTo}
                  onRefreshBalance={refreshBalance}
                  refreshing={refreshing}
                />
              )}
            </div>
            <div className='bk-sec-foot'>
              {ci > 0 ? (
                <button type='button' className='bk-btn quiet' onClick={() => goTo(steps[ci - 1])}>
                  <Ic name='arrow-left' /> Back
                </button>
              ) : (
                <span />
              )}
              <div className='right'>
                {current === 'review' && (
                  <button type='button' className='bk-btn secondary' onClick={saveDraft} disabled={savingDraft}>
                    Save as draft
                  </button>
                )}
                <button type='button' className='bk-btn primary js-primary' onClick={proceed} disabled={primaryDisabled} aria-busy={submitting}>
                  {primaryLabel()}
                  {current !== 'review' && (
                    <>
                      <span className='bk-sr'> to {nextName}</span>
                      <Ic name='arrow-right' />
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
          {k === 'invalid' && current !== 'route' && (
            <Note tone='bad' icon='circle-alert'>
              <p>One end of the journey must be Nigeria.</p>
            </Note>
          )}
        </div>

        <aside className='bk-ticket-col' aria-label='Price'>
          <Ticket estimate={estimate} final={final} onReview={onReview} onRetry={fetchFinal} inputs={priceInputs} headingLevel />
        </aside>

        <div className='bk-bb'>
          {bbOpen && (
            <div className='bk-bb-panel' id='bk-bb-panel'>
              <Ticket estimate={estimate} final={final} onReview={onReview} onRetry={fetchFinal} inputs={priceInputs} />
            </div>
          )}
          <div className='bk-bb-row'>
            <button type='button' className='bk-bb-price' aria-expanded={bbOpen} aria-controls='bk-bb-panel' onClick={() => setBbOpen((o) => !o)}>
              <span className='bp-t'>
                <small>{shown.label}</small>
                <b>{shown.value}</b>
              </span>
              <Ic name='chevron-up' />
              <span className='bk-sr'>, {bbOpen ? 'hide' : 'show'} price breakdown</span>
            </button>
            <button type='button' className='bk-btn primary' onClick={proceed} disabled={primaryDisabled} aria-busy={submitting}>
              {primaryLabel()}
              {current !== 'review' && <Ic name='arrow-right' />}
            </button>
          </div>
        </div>
        <div className='bk-sr' aria-live='polite'>
          {live}
        </div>
      </div>
    </Ctx.Provider>
  )
}
